/**
 * useMethods Hook
 *
 * React hook for API calls:
 * - Fetch methods with filters
 * - Fetch single method detail
 * - Fetch filters (modules, topics, tags)
 * - Loading, error, and success states
 * - Debounce filter changes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Method, Filters, FiltersResponse, MethodsResponse } from '../types';

// Get settings from global window object
const getSettings = (): { apiUrl: string; restNonce: string; currentLang: string } => {
  if (typeof window !== 'undefined' && window.tpSettings) {
    return {
      apiUrl: window.tpSettings.apiUrl,
      restNonce: window.tpSettings.restNonce ?? '',
      currentLang: window.tpSettings.currentLang,
    };
  }
  // Fallback for tests
  return {
    apiUrl: '/wp-json/training-planner/v1',
    restNonce: '',
    currentLang: 'en',
  };
};

/**
 * Hook for fetching methods list with filters
 */
export const useMethods = (initialFilters: Partial<Filters> = {}) => {
  const [methods, setMethods] = useState<Method[]>([]);
  const [filters, setFilters] = useState<Filters>({
    lang: getSettings().currentLang,
    ...initialFilters,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Track whether this is the initial mount (skip debounce for first fetch)
  const isInitialMount = useRef(true);

  // Fetch methods
  const fetchMethods = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const settings = getSettings();
      const params = new URLSearchParams();

      // Add filters to params
      if (filters.module) params.set('module', filters.module.toString());
      if (filters.topics?.length) {
        filters.topics.forEach(t => params.append('topic_search[]', t));
      }
      if (filters.tag) params.set('tag', filters.tag.toString());
      if (filters.contributor)
        params.set('contributor', filters.contributor.toString());
      if (filters.min_duration)
        params.set('min_duration', filters.min_duration.toString());
      if (filters.max_duration)
        params.set('max_duration', filters.max_duration.toString());
      params.set('lang', filters.lang);

      const url = `${settings.apiUrl}/methods?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          'X-WP-Nonce': settings.restNonce,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: MethodsResponse = await response.json();

      if (data.success) {
        setMethods(data.data);
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch methods');
      setMethods([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch methods when filters change
  // Initial load fetches immediately; subsequent changes are debounced (300ms)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchMethods();
      return;
    }

    const timer = setTimeout(() => {
      fetchMethods();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [filters]);

  return { methods, filters, setFilters, loading, error };
};

/**
 * Hook for fetching single method detail
 * @param methodId - The ID of the method to fetch
 * @param lang - The language code for the method content (e.g., 'en', 'de', 'pl', 'da', 'uk', 'cs', 'tr', 'ar', 'nl')
 */
export const useMethodDetail = (methodId: number | null, lang?: string) => {
  const [method, setMethod] = useState<Method | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!methodId) {
      setMethod(null);
      return;
    }

    const fetchMethod = async () => {
      setLoading(true);
      setError(null);

      try {
        const settings = getSettings();
        // Build URL with language parameter for proper translation
        const currentLang = lang || settings.currentLang;
        const url = `${settings.apiUrl}/methods/${methodId}?lang=${currentLang}`;
        const response = await fetch(url, {
          headers: {
            'X-WP-Nonce': settings.restNonce,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setMethod(data.data);
        } else {
          throw new Error('API returned success: false');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch method'
        );
        setMethod(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMethod();
  }, [methodId, lang]);

  return { method, loading, error };
};

/**
 * Hook for fetching filters (modules, tags, duration range)
 * @param lang - Optional language code. When provided, filters are fetched
 *   for that language. Defaults to the current WordPress language.
 *   Re-fetches automatically when the language changes.
 */
export const useFilters = (lang?: string) => {
  const [filtersData, setFiltersData] = useState<
    FiltersResponse['data'] | null
  >(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFilters = async () => {
      setLoading(true);
      setError(null);

      try {
        const settings = getSettings();
        const effectiveLang = lang || settings.currentLang;
        const url = `${settings.apiUrl}/filters?lang=${effectiveLang}`;
        const response = await fetch(url, {
          headers: {
            'X-WP-Nonce': settings.restNonce,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: FiltersResponse = await response.json();

        if (data.success) {
          setFiltersData(data.data);
        } else {
          throw new Error('API returned success: false');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch filters'
        );
        setFiltersData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, [lang]);

  return { filtersData, loading, error };
};
